import echo from '@/config/echo'
import notificacoesService from '@/services/notificacoes'
import alertasAgendamentosService from '@/services/alertas-agendamentos'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

/**
 * Mixin para gerenciar notificações em tempo real
 * Uso em componentes Vue 2:
 *
 * import notificacoesMixin from '@/mixins/notificacoes'
 *
 * export default {
 *   mixins: [notificacoesMixin],
 *   // ...
 * }
 */
export default {
  data() {
    return {
      notificacoes: [],
      unreadCount: 0,
      isConnected: false,
      loading: false,
      echoChannel: null,
      alertasAtivos: true, // Controla se os alertas de agendamentos próximos estão ativos
    }
  },

  computed: {
    userId() {
      return localStorage.getItem('funcionarioId') || localStorage.getItem('userId')
    },
  },

  mounted() {
    this.inicializarNotificacoes()
    this.inicializarAlertasAgendamentos()
  },

  beforeDestroy() {
    this.desconectarWebSocket()
    this.pararAlertasAgendamentos()
  },

  methods: {
    /**
     * Inicializa o sistema de notificações
     */
    async inicializarNotificacoes() {
      await this.carregarNotificacoes()
      this.conectarWebSocket()
    },

    /**
     * Carrega notificações via API REST
     * @param {Object} params - Parâmetros de filtro
     * @param {string} params.status - 'all' ou 'unread' (padrão: 'all')
     * @param {string} params.type - Tipo da notificação (opcional)
     * @param {number} params.per_page - Itens por página, 1-100 (padrão: 20)
     * @param {number} params.page - Página (padrão: 1)
     */
    async carregarNotificacoes(params = { status: 'all', per_page: 20 }) {
      this.loading = true
      try {
        const response = await notificacoesService.listar(params)

        // Extrair notificações da estrutura paginada
        // Resposta: { unread_count, data: { data: Array, total, ... } }
        const notificacoesList = response.data?.data || []
        this.notificacoes = notificacoesList

        // Usar contagem fornecida pela API
        this.unreadCount = response.unread_count || 0
      } catch (error) {
        // Erro ao carregar notificações
        if (this.$bvToast) {
          this.$bvToast.toast('Erro ao carregar notificações', {
            title: 'Erro',
            variant: 'danger',
            solid: true,
          })
        }
      } finally {
        this.loading = false
      }
    },

    /**
     * Conecta ao WebSocket e escuta eventos
     */
    conectarWebSocket() {
      if (!this.userId) {
        return
      }

      try {
        // Conectar ao canal privado do usuário
        this.echoChannel = echo.private(`users.${this.userId}`)

        // Escutar evento de agendamento criado
        this.echoChannel
          .listen('.agendamentos.criado', event => {
            this.handleNovaNotificacao(event)
          })
          .subscribed(() => {
            this.isConnected = true
          })
          .error(() => {
            this.isConnected = false
          })
      } catch (error) {
        this.isConnected = false
      }
    },

    /**
     * Desconecta do WebSocket
     */
    desconectarWebSocket() {
      if (this.echoChannel && this.userId) {
        echo.leave(`users.${this.userId}`)
        this.isConnected = false
      }
    },

    /**
     * Processa nova notificação recebida via WebSocket
     */
    handleNovaNotificacao(event) {
      // Recarregar lista de notificações
      this.carregarNotificacoes()

      // Mostrar toast de notificação
      const tipo = event.payload && event.payload.tipo ? event.payload.tipo : ''
      if (this.$bvToast) {
        this.$bvToast.toast(
          `Novo agendamento ${tipo}`,
          {
            title: 'Nova Notificação',
            variant: 'primary',
            solid: true,
            autoHideDelay: 5000,
          },
        )
      }

      // Opcional: Tocar som
      this.tocarSomNotificacao()

      // Opcional: Notificação do navegador
      this.mostrarNotificacaoNavegador(event)
    },

    /**
     * Marca notificação como lida
     */
    async marcarComoLida(notificationId) {
      try {
        await notificacoesService.marcarComoLida(notificationId)
        await this.carregarNotificacoes()
      } catch (error) {
        // Erro ao marcar como lida
      }
    },

    /**
     * Marca todas as notificações como lidas
     */
    async marcarTodasComoLidas() {
      try {
        await notificacoesService.marcarTodasComoLidas()
        await this.carregarNotificacoes()
        if (this.$bvToast) {
          this.$bvToast.toast('Todas as notificações foram marcadas como lidas', {
            title: 'Sucesso',
            variant: 'success',
            solid: true,
          })
        }
      } catch (error) {
        // Erro ao marcar todas como lidas
      }
    },

    /**
     * Toca som de notificação
     */
    tocarSomNotificacao() {
      try {
        const audio = new Audio('/notification.mp3')
        audio.play().catch(() => {
          // Som não é crítico, apenas ignora o erro
        })
      } catch (error) {
        // Som não é crítico, apenas ignora o erro
      }
    },

    /**
     * Mostra notificação do navegador
     */
    mostrarNotificacaoNavegador(event) {
      if (!('Notification' in window)) return

      const tipo = event.payload && event.payload.tipo ? event.payload.tipo : 'N/A'

      if (Notification.permission === 'granted') {
        // eslint-disable-next-line no-new
        new Notification('Novo Agendamento', {
          body: `Tipo: ${tipo}`,
          icon: '/logo.png',
        })
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            // eslint-disable-next-line no-new
            new Notification('Novo Agendamento', {
              body: `Tipo: ${tipo}`,
              icon: '/logo.png',
            })
          }
        })
      }
    },

    /**
     * Inicializa o sistema de alertas de agendamentos próximos
     */
    inicializarAlertasAgendamentos() {
      if (!this.alertasAtivos) return

      // Configurar o serviço (opcional - usar valores padrão ou customizar)
      alertasAgendamentosService.configurar({
        minutosAntecedencia: 20, // Alertar 20 minutos antes do agendamento
        intervaloVerificacaoMs: 30000, // Verificar a cada 30 segundos para mais precisão
        alertarNovamente: false, // Não alertar o mesmo agendamento múltiplas vezes
      })

      // Registrar callback para mostrar toast quando houver agendamento próximo
      alertasAgendamentosService.onAlerta(this.handleAlertaAgendamentoProximo.bind(this))

      // Iniciar verificação periódica
      alertasAgendamentosService.iniciar()
    },

    /**
     * Para o sistema de alertas de agendamentos
     */
    pararAlertasAgendamentos() {
      alertasAgendamentosService.offAlerta(this.handleAlertaAgendamentoProximo)
      alertasAgendamentosService.parar()
    },

    /**
     * Manipula alerta de agendamento próximo
     * @param {Object} alerta - { agendamento, minutosRestantes, dataAgendamento }
     */
    handleAlertaAgendamentoProximo(alerta) {
      const { agendamento, minutosRestantes } = alerta

      // Formatar data do agendamento
      const dataFormatada = format(alerta.dataAgendamento, "dd/MM/yyyy 'às' HH:mm", {
        locale: ptBR,
      })

      // Extrair informações do agendamento
      const tipoAgendamento = agendamento.tipo || 'Agendamento'
      const clienteNome = agendamento.cliente_nome || agendamento.cliente_id || 'Cliente'
      const atendenteNome = agendamento.atendente_nome || 'Sistema'

      // Definir variante do toast baseado no tempo restante
      let variant = 'warning'
      let iconEmoji = '⏰'

      if (minutosRestantes <= 5) {
        variant = 'danger'
        iconEmoji = '🚨'
      } else if (minutosRestantes <= 10) {
        variant = 'warning'
        iconEmoji = '⚠️'
      }

      // Construir mensagem detalhada
      const mensagem = [
        `${tipoAgendamento} agendado`,
        `Cliente: ${clienteNome}`,
        `Atendente: ${atendenteNome}`,
        `Horário: ${dataFormatada}`,
        `Em ${minutosRestantes} minuto(s)`,
      ].join('\n')

      // Mostrar toast de alerta com conteúdo formatado
      if (this.$bvToast) {
        this.$bvToast.toast(
          mensagem,
          {
            title: `${iconEmoji} Agendamento Próximo`,
            variant,
            solid: true,
            autoHideDelay: 10000, // 10 segundos
            toaster: 'b-toaster-top-right',
            static: false,
          },
        )
      }

      // Tocar som de alerta
      this.tocarSomNotificacao()

      // Notificação do navegador
      this.mostrarNotificacaoNavegadorAgendamento(alerta)
    },

    /**
     * Mostra notificação do navegador para agendamento próximo
     */
    mostrarNotificacaoNavegadorAgendamento(alerta) {
      if (!('Notification' in window)) return

      const { agendamento, minutosRestantes } = alerta
      const dataFormatada = format(alerta.dataAgendamento, "dd/MM/yyyy 'às' HH:mm", {
        locale: ptBR,
      })

      const tipoAgendamento = agendamento.tipo || 'Agendamento'
      const clienteNome = agendamento.cliente_nome || agendamento.cliente_id || 'Cliente'
      const atendenteNome = agendamento.atendente_nome || 'Sistema'

      const body = [
        `${tipoAgendamento}`,
        `Cliente: ${clienteNome}`,
        `Atendente: ${atendenteNome}`,
        `Horário: ${dataFormatada}`,
        `Em ${minutosRestantes} minuto(s)`,
      ].join('\n')

      if (Notification.permission === 'granted') {
        // eslint-disable-next-line no-new
        new Notification('⏰ Agendamento Próximo', {
          body,
          icon: '/logo.png',
          requireInteraction: true, // Manter notificação visível
          tag: `agendamento-${agendamento.id}`, // Evitar duplicatas
        })
      }
    },

    /**
     * Ativar/desativar alertas de agendamentos próximos
     */
    toggleAlertasAgendamentos(ativo) {
      this.alertasAtivos = ativo
      if (ativo) {
        this.inicializarAlertasAgendamentos()
      } else {
        this.pararAlertasAgendamentos()
      }
    },
  },
}
